import { useEffect, useRef, type ReactNode } from "react";
import { CloudOff, LoaderCircle, Lock, PlugZap, ShieldX, UserRoundSearch } from "lucide-react";
import { Button, EmptyState } from "@codelittinc/carbon-design-system";
import { trackApp, type SignInArea } from "../lib/analytics";
import { beginSignIn, clearSignInAttempt } from "../lib/signin";
import { TicketLink } from "./TicketLink";

/**
 * The panels a page shows instead of its data, shared by My tools and
 * Training so both fail, refuse and recover in the same words and the same way.
 */

export type FocusRef = (element: HTMLElement | null) => void;

export const ACTION = "h-11 sm:h-9";
export const ICON = "size-6";

/**
 * After Try again, focus moves to whatever the page now shows, so the
 * result is announced without a live region shouting over the panel.
 */
export function useRetryFocus(
  state: { kind: string; retrying?: boolean },
  retry: () => void,
): { focusRef: FocusRef; onRetry: () => void } {
  const focusTarget = useRef<HTMLElement | null>(null);
  const retried = useRef(false);
  const focusRef: FocusRef = (element) => {
    focusTarget.current = element;
  };

  useEffect(() => {
    if (!retried.current || state.kind === "loading") return;
    if (state.kind === "unavailable" && state.retrying) return;
    retried.current = false;
    focusTarget.current?.focus();
  }, [state]);

  const onRetry = () => {
    retried.current = true;
    retry();
  };

  return { focusRef, onRetry };
}

export function Panel({ focusRef, children }: { focusRef: FocusRef; children: ReactNode }) {
  return (
    <div ref={focusRef} tabIndex={-1} className="outline-none">
      {children}
    </div>
  );
}

/**
 * Account switching opens the shared confirmation page; cleanup happens after confirmation.
 */
export function SwitchAccount({ href = "/auth/signout" }: { href?: string }) {
  return (
    <Button asChild size="lg" className={ACTION}>
      <a href={href === "/auth/signout" ? "/sign-out" : href}>
        Sign in with a different account
      </a>
    </Button>
  );
}

export function Unavailable({
  title,
  description,
  retrying,
  retry,
  focusRef,
}: {
  title: string;
  description: string;
  retrying: boolean;
  retry: () => void;
  focusRef: FocusRef;
}) {
  return (
    <Panel focusRef={focusRef}>
      <EmptyState
        icon={<CloudOff className={ICON} />}
        title={title}
        description={description}
        action={
          <Button size="lg" className={ACTION} disabled={retrying} onClick={retry}>
            {retrying ? (
              <>
                <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
                Trying again…
              </>
            ) : (
              "Try again"
            )}
          </Button>
        }
      />
    </Panel>
  );
}

/** `area` counts the click as `signin_again_clicked`; none where PostHog never starts. */
export function SignInAgain({ focusRef, area }: { focusRef: FocusRef; area?: SignInArea }) {
  return (
    <Panel focusRef={focusRef}>
      <EmptyState
        icon={<Lock className={ICON} />}
        title="Please sign in again"
        description="We couldn't confirm your session."
        action={
          <Button
            size="lg"
            className={ACTION}
            onClick={() => {
              if (area) trackApp("signin_again_clicked", { area });
              clearSignInAttempt(window.sessionStorage);
              beginSignIn(window.location.href, window.sessionStorage, (url) => window.location.assign(url));
            }}
          >
            Sign in
          </Button>
        }
      />
    </Panel>
  );
}

export function WrongAccount({
  signedInAs,
  signOutUrl,
  focusRef,
}: {
  signedInAs: string | null;
  signOutUrl: string;
  focusRef: FocusRef;
}) {
  return (
    <Panel focusRef={focusRef}>
      <EmptyState
        icon={<ShieldX className={ICON} />}
        title="Verify your email address first"
        description={`${signedInAs ? `You're signed in as ${signedInAs}. ` : ""}Your account has no verified email address. Sign in with a verified work account.`}
        action={<SwitchAccount href={signOutUrl} />}
      />
    </Panel>
  );
}

/**
 * Signed in and verified, but not a person in Gatekeeper: Carbon OS shows them
 * nothing else, on any page. The way out is another account or a ticket.
 */
export function NotInGatekeeper({
  signedInAs,
  signOutUrl = "/auth/signout",
  accessRequestUrl,
  focusRef,
}: {
  signedInAs: string | null;
  signOutUrl?: string;
  accessRequestUrl: string | null;
  focusRef: FocusRef;
}) {
  return (
    <Panel focusRef={focusRef}>
      <EmptyState
        icon={<UserRoundSearch className={ICON} />}
        title="We couldn't find you in Carbon's staff directory"
        description={`${signedInAs ? `You're signed in as ${signedInAs}, and the` : "The"} staff directory doesn't recognise that address. If you have another work account, sign in with that one. Otherwise, raise a ticket with IT.`}
        action={
          <div className="flex flex-col gap-2 sm:flex-row">
            <SwitchAccount href={signOutUrl} />
            {accessRequestUrl ? <TicketLink href={accessRequestUrl} variant="outline" className={ACTION} /> : null}
          </div>
        }
      />
    </Panel>
  );
}

export function NotConnected({ message, focusRef }: { message: string; focusRef: FocusRef }) {
  return (
    <Panel focusRef={focusRef}>
      <EmptyState icon={<PlugZap className={ICON} />} title="Not connected to Gatekeeper" description={message} />
    </Panel>
  );
}
